import React, { useEffect, useState, useRef, Children, Component } from 'react';
import { motion, useInView, useMotionValue, useTransform, animate } from 'framer-motion';
import { ArrowRightIcon, FlameIcon, UsersIcon, TreesIcon, GlobeIcon, ShieldCheckIcon, ScaleIcon, LayoutDashboardIcon, FolderIcon, CpuIcon, FileTextIcon, SettingsIcon, ActivityIcon, MapPinIcon, CheckCircle2Icon, ClockIcon } from 'lucide-react';
import { Footer } from '../components/Footer';
import { HowCarbonSafeWorks } from '../components/HowCarbonSafeWorks';
const COOKSTOVE_IMG = 'https://www.engineeringforchange.org/wp-content/uploads/2018/02/ace1stove-1_resized.jpg';
/* ─── Hexagon Visual with Cookstove Image ─── */
function CookstoveHexagonVisual() {
  return <div className="relative w-full max-w-lg mx-auto aspect-square" data-id="element-1246">
      <svg className="absolute inset-0 w-full h-full opacity-[0.07]" aria-hidden="true" data-id="element-1247">
        <defs data-id="element-1248">
          <pattern id="cook-grid" width="40" height="40" patternUnits="userSpaceOnUse" patternTransform="rotate(45)" data-id="element-1249">
            <line x1="0" y1="0" x2="0" y2="40" stroke="#1B4332" strokeWidth="0.5" data-id="element-1250" />
            <line x1="0" y1="0" x2="40" y2="0" stroke="#1B4332" strokeWidth="0.5" data-id="element-1251" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#cook-grid)" data-id="element-1252" />
      </svg>

      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 400" aria-hidden="true" data-id="element-1253">
        <defs data-id="element-1254">
          <radialGradient id="cook-glow" cx="50%" cy="50%" r="50%" data-id="element-1255">
            <stop offset="0%" stopColor="#52B788" stopOpacity="0.15" data-id="element-1256" />
            <stop offset="100%" stopColor="#52B788" stopOpacity="0" data-id="element-1257" />
          </radialGradient>
          <clipPath id="hex-clip" data-id="element-1258">
            <polygon points="200,100 300,155 300,245 200,300 100,245 100,155" data-id="element-1259" />
          </clipPath>
        </defs>
        <circle cx="200" cy="200" r="180" fill="url(#cook-glow)" data-id="element-1260" />
        <polygon points="200,30 355,110 355,290 200,370 45,290 45,110" fill="none" stroke="#52B788" strokeWidth="1.5" opacity="0.3" data-id="element-1261" />
        <polygon points="200,70 325,135 325,265 200,330 75,265 75,135" fill="none" stroke="#52B788" strokeWidth="1" opacity="0.2" data-id="element-1262" />
        <polygon points="200,100 300,155 300,245 200,300 100,245 100,155" fill="#52B788" fillOpacity="0.08" stroke="#52B788" strokeWidth="2" opacity="0.5" data-id="element-1263" />
        {/* Connection dots */}
        <circle cx="200" cy="30" r="5" fill="#52B788" opacity="0.6" data-id="element-1264" />
        <circle cx="355" cy="110" r="4" fill="#9CA3AF" opacity="0.5" data-id="element-1265" />
        <circle cx="355" cy="290" r="5" fill="#52B788" opacity="0.6" data-id="element-1266" />
        <circle cx="200" cy="370" r="4" fill="#9CA3AF" opacity="0.5" data-id="element-1267" />
        <circle cx="45" cy="290" r="5" fill="#52B788" opacity="0.6" data-id="element-1268" />
        <circle cx="45" cy="110" r="4" fill="#9CA3AF" opacity="0.5" data-id="element-1269" />
        <circle cx="278" cy="70" r="3" fill="#9CA3AF" opacity="0.4" data-id="element-1270" />
        <circle cx="355" cy="200" r="3" fill="#9CA3AF" opacity="0.4" data-id="element-1271" />
        <circle cx="278" cy="330" r="3" fill="#9CA3AF" opacity="0.4" data-id="element-1272" />
        <circle cx="122" cy="330" r="3" fill="#9CA3AF" opacity="0.4" data-id="element-1273" />
        <circle cx="45" cy="200" r="3" fill="#9CA3AF" opacity="0.4" data-id="element-1274" />
        <circle cx="122" cy="70" r="3" fill="#9CA3AF" opacity="0.4" data-id="element-1275" />
        {/* Cookstove image clipped to inner hexagon */}
        <image href={COOKSTOVE_IMG} x="90" y="90" width="220" height="220" clipPath="url(#hex-clip)" preserveAspectRatio="xMidYMid slice" data-id="element-1276" />
        {/* Re-draw inner hexagon border on top of image */}
        <polygon points="200,100 300,155 300,245 200,300 100,245 100,155" fill="none" stroke="#52B788" strokeWidth="2.5" opacity="0.7" data-id="element-1277" />
      </svg>

      {/* Floating badges */}
      <motion.div className="absolute top-6 left-1/2 -translate-x-1/2 w-11 h-11 rounded-full bg-carbon-bright flex items-center justify-center shadow-lg shadow-carbon-bright/30" animate={{
      y: [0, -6, 0]
    }} transition={{
      duration: 3,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-1278">
        <span className="text-white text-lg" data-id="element-1279">🏠</span>
      </motion.div>

      <motion.div className="absolute bottom-16 right-4 w-12 h-12 rounded-full bg-carbon-deep flex items-center justify-center shadow-lg" animate={{
      y: [0, 6, 0]
    }} transition={{
      duration: 3.5,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: 0.5
    }} data-id="element-1280">
        <span className="text-white text-lg" data-id="element-1281">📊</span>
      </motion.div>

      <motion.div className="absolute bottom-8 left-8 w-11 h-11 rounded-full bg-carbon-bright/90 flex items-center justify-center shadow-lg shadow-carbon-bright/30" animate={{
      y: [0, -5, 0]
    }} transition={{
      duration: 4,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: 1
    }} data-id="element-1282">
        <span className="text-white text-lg" data-id="element-1283">🌍</span>
      </motion.div>
    </div>;
}
const iconMap: Record<string, ComponentType<{
  className?: string;
}>> = {
  FlameIcon,
  UsersIcon,
  TreesIcon,
  GlobeIcon,
  ShieldCheckIcon,
  ScaleIcon
};
/* ─── Animated Stat ─── */
function AnimatedStat({
  value,
  label,
  detail,
  light
}: {
  value: string;
  label: string;
  detail: string;
  light?: boolean;
}) {
  const numericMatch = value.match(/^([\d.]+)(.*)$/);
  const numericValue = numericMatch ? parseFloat(numericMatch[1]) : 0;
  const suffix = numericMatch ? numericMatch[2] : value;
  const isNumeric = !!numericMatch;
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-40px'
  });
  const mv = useMotionValue(0);
  const rounded = useTransform(mv, v => {
    if (numericValue < 10) return Number(v.toFixed(1));
    return Math.round(v);
  });
  useEffect(() => {
    if (isInView && isNumeric) {
      const controls = animate(mv, numericValue, {
        duration: 2,
        ease: [0.22, 1, 0.36, 1]
      });
      return controls.stop;
    }
  }, [isInView, numericValue, mv, isNumeric]);
  useEffect(() => {
    const unsub = rounded.on('change', v => setDisplay(v));
    return unsub;
  }, [rounded]);
  return <div ref={ref} className="text-center" data-id="element-1284">
      <p className={`text-4xl sm:text-5xl font-extrabold ${light ? 'text-carbon-deep' : 'text-carbon-bright'}`} data-id="element-1285">
        {isNumeric ? `${display.toLocaleString()}${suffix}` : value}
      </p>
      <p className={`mt-2 text-base font-bold ${light ? 'text-carbon-deep' : 'text-carbon-deep'}`} data-id="element-1286">
        {label}
      </p>
      <p className="mt-1 text-sm text-carbon-mid leading-relaxed" data-id="element-1287">{detail}</p>
    </div>;
}
/* ─── Dashboard Mockup ─── */
function DashboardMockup() {
  const barHeights = [65, 82, 45, 90, 72, 58, 88];
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  return <div className="rounded-2xl shadow-2xl border border-carbon-geo overflow-hidden bg-white" style={{
    transform: 'perspective(1200px) rotateX(2deg) rotateY(-1deg) scale(0.88)'
  }} data-id="element-1288">
      {/* Browser Chrome */}
      <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 border-b border-gray-200" data-id="element-1289">
        <div className="flex gap-1.5" data-id="element-1290">
          <div className="w-3 h-3 rounded-full bg-red-400" data-id="element-1291" />
          <div className="w-3 h-3 rounded-full bg-yellow-400" data-id="element-1292" />
          <div className="w-3 h-3 rounded-full bg-green-400" data-id="element-1293" />
        </div>
        <div className="flex-1 mx-4" data-id="element-1294">
          <div className="bg-white rounded-md px-3 py-1 text-xs text-gray-500 font-mono border border-gray-200 max-w-md" data-id="element-1295">
            app.carbonsafe.io/cookstoves
          </div>
        </div>
      </div>

      <div className="flex min-h-[420px]" data-id="element-1296">
        {/* Sidebar */}
        <div className="w-48 bg-carbon-deep flex-shrink-0 p-3 hidden sm:block" data-id="element-1297">
          <div className="flex items-center gap-2 px-2 py-2 mb-4" data-id="element-1298">
            <div className="w-7 h-7 rounded-lg bg-carbon-bright flex items-center justify-center" data-id="element-1299">
              <span className="text-white text-xs font-bold" data-id="element-1300">CS</span>
            </div>
            <span className="text-white text-sm font-bold" data-id="element-1301">CarbonSafe</span>
          </div>
          <nav className="space-y-0.5" data-id="element-1302">
            <div className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg bg-white/10 text-white text-xs font-medium" data-id="element-1303">
              <LayoutDashboardIcon className="w-3.5 h-3.5" data-id="element-1304" />
              Dashboard
            </div>
            <div className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-white/50 text-xs" data-id="element-1305">
              <FolderIcon className="w-3.5 h-3.5" data-id="element-1306" />
              Projects
            </div>
            <div className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-white/50 text-xs" data-id="element-1307">
              <CpuIcon className="w-3.5 h-3.5" data-id="element-1308" />
              Devices
            </div>
            <div className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-white/50 text-xs" data-id="element-1309">
              <FileTextIcon className="w-3.5 h-3.5" data-id="element-1310" />
              Reports
            </div>
            <div className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-white/50 text-xs" data-id="element-1311">
              <SettingsIcon className="w-3.5 h-3.5" data-id="element-1312" />
              Settings
            </div>
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-4 sm:p-5 bg-gray-50 overflow-hidden" data-id="element-1313">
          {/* Header */}
          <div className="flex items-center justify-between mb-4" data-id="element-1314">
            <div data-id="element-1315">
              <h3 className="text-sm font-bold text-carbon-deep" data-id="element-1316">
                Cookstove Project | Mbale District
              </h3>
              <p className="text-[11px] text-gray-400 mt-0.5" data-id="element-1317">
                Last updated 12 min ago
              </p>
            </div>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold" data-id="element-1318">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-1319" />
              Active
            </span>
          </div>

          {/* Mini Stat Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mb-4" data-id="element-1320">
            <div className="bg-white rounded-lg p-3 border border-gray-100" data-id="element-1321">
              <p className="text-[10px] text-gray-400 font-medium" data-id="element-1322">
                Stoves Deployed
              </p>
              <p className="text-lg font-bold text-carbon-deep mt-0.5" data-id="element-1323">1,247</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-100" data-id="element-1324">
              <p className="text-[10px] text-gray-400 font-medium" data-id="element-1325">
                Active Rate
              </p>
              <p className="text-lg font-bold text-carbon-bright mt-0.5" data-id="element-1326">89%</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-100" data-id="element-1327">
              <p className="text-[10px] text-gray-400 font-medium" data-id="element-1328">
                tCO₂e Reduced
              </p>
              <p className="text-lg font-bold text-carbon-deep mt-0.5" data-id="element-1329">3,412</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-100" data-id="element-1330">
              <p className="text-[10px] text-gray-400 font-medium" data-id="element-1331">
                Credits Generated
              </p>
              <p className="text-lg font-bold text-carbon-deep mt-0.5" data-id="element-1332">
                $68,240
              </p>
            </div>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5 mb-4" data-id="element-1333">
            {/* Bar Chart */}
            <div className="bg-white rounded-lg p-3 border border-gray-100" data-id="element-1334">
              <div className="flex items-center justify-between mb-3" data-id="element-1335">
                <p className="text-[11px] font-semibold text-carbon-deep" data-id="element-1336">
                  Daily Usage Hours
                </p>
                <p className="text-[10px] text-gray-400" data-id="element-1337">This week</p>
              </div>
              <div className="flex items-end gap-2 h-24" data-id="element-1338">
                {barHeights.map((h, i) => <div key={i} className="flex-1 flex flex-col items-center gap-1" data-id="element-1339">
                    <div className="w-full rounded-t-sm bg-carbon-bright/80" style={{
                  height: `${h}%`
                }} data-id="element-1340" />
                    <span className="text-[9px] text-gray-400" data-id="element-1341">{days[i]}</span>
                  </div>)}
              </div>
            </div>

            {/* Map Placeholder */}
            <div className="bg-white rounded-lg p-3 border border-gray-100" data-id="element-1342">
              <div className="flex items-center justify-between mb-3" data-id="element-1343">
                <p className="text-[11px] font-semibold text-carbon-deep" data-id="element-1344">
                  Stove Locations
                </p>
                <p className="text-[10px] text-gray-400" data-id="element-1345">Mbale District</p>
              </div>
              <div className="relative h-24 rounded-lg bg-emerald-50 overflow-hidden" data-id="element-1346">
                {/* Fake map grid */}
                <div className="absolute inset-0 opacity-20" data-id="element-1347">
                  {[...Array(6)].map((_, i) => <div key={i} className="absolute border-b border-r border-emerald-300" style={{
                  left: `${i * 20}%`,
                  top: 0,
                  width: '1px',
                  height: '100%'
                }} data-id="element-1348" />)}
                  {[...Array(4)].map((_, i) => <div key={i} className="absolute border-b border-emerald-300" style={{
                  top: `${i * 33}%`,
                  left: 0,
                  width: '100%',
                  height: '1px'
                }} data-id="element-1349" />)}
                </div>
                {/* Dots */}
                <div className="absolute w-2 h-2 rounded-full bg-carbon-bright shadow-sm" style={{
                left: '20%',
                top: '30%'
              }} data-id="element-1350" />
                <div className="absolute w-2.5 h-2.5 rounded-full bg-carbon-bright shadow-sm" style={{
                left: '45%',
                top: '50%'
              }} data-id="element-1351" />
                <div className="absolute w-2 h-2 rounded-full bg-carbon-bright shadow-sm" style={{
                left: '65%',
                top: '25%'
              }} data-id="element-1352" />
                <div className="absolute w-1.5 h-1.5 rounded-full bg-carbon-bright/70 shadow-sm" style={{
                left: '35%',
                top: '70%'
              }} data-id="element-1353" />
                <div className="absolute w-2 h-2 rounded-full bg-carbon-bright shadow-sm" style={{
                left: '75%',
                top: '60%'
              }} data-id="element-1354" />
                <div className="absolute w-1.5 h-1.5 rounded-full bg-carbon-bright/70 shadow-sm" style={{
                left: '55%',
                top: '40%'
              }} data-id="element-1355" />
                <div className="absolute w-2 h-2 rounded-full bg-carbon-bright shadow-sm" style={{
                left: '25%',
                top: '55%'
              }} data-id="element-1356" />
                <div className="absolute w-1.5 h-1.5 rounded-full bg-amber-500 shadow-sm" style={{
                left: '80%',
                top: '35%'
              }} data-id="element-1357" />
              </div>
            </div>
          </div>

          {/* Activity Table */}
          <div className="bg-white rounded-lg border border-gray-100 overflow-hidden" data-id="element-1358">
            <div className="px-3 py-2 border-b border-gray-100" data-id="element-1359">
              <p className="text-[11px] font-semibold text-carbon-deep" data-id="element-1360">
                Recent Activity
              </p>
            </div>
            <div className="divide-y divide-gray-50" data-id="element-1361">
              <div className="flex items-center gap-3 px-3 py-2" data-id="element-1362">
                <ActivityIcon className="w-3 h-3 text-carbon-bright flex-shrink-0" data-id="element-1363" />
                <span className="text-[11px] text-carbon-deep font-medium flex-1" data-id="element-1364">
                  Stove #1247
                </span>
                <span className="text-[10px] text-gray-500" data-id="element-1365">
                  Usage detected: 2.4 hrs
                </span>
                <span className="text-[10px] text-gray-400 flex items-center gap-1" data-id="element-1366">
                  <ClockIcon className="w-2.5 h-2.5" data-id="element-1367" />
                  12m ago
                </span>
              </div>
              <div className="flex items-center gap-3 px-3 py-2" data-id="element-1368">
                <CheckCircle2Icon className="w-3 h-3 text-emerald-500 flex-shrink-0" data-id="element-1369" />
                <span className="text-[11px] text-carbon-deep font-medium flex-1" data-id="element-1370">
                  Stove #1198
                </span>
                <span className="text-[10px] text-gray-500" data-id="element-1371">
                  Fuel savings verified: 1.8 kg
                </span>
                <span className="text-[10px] text-gray-400 flex items-center gap-1" data-id="element-1372">
                  <ClockIcon className="w-2.5 h-2.5" data-id="element-1373" />
                  34m ago
                </span>
              </div>
              <div className="flex items-center gap-3 px-3 py-2" data-id="element-1374">
                <ShieldCheckIcon className="w-3 h-3 text-blue-500 flex-shrink-0" data-id="element-1375" />
                <span className="text-[11px] text-carbon-deep font-medium flex-1" data-id="element-1376">
                  Batch #47
                </span>
                <span className="text-[10px] text-gray-500" data-id="element-1377">
                  Credits issued: 142 tCO₂e
                </span>
                <span className="text-[10px] text-gray-400 flex items-center gap-1" data-id="element-1378">
                  <ClockIcon className="w-2.5 h-2.5" data-id="element-1379" />
                  2h ago
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
/* ─── Emission Reduction Chart ─── */
function EmissionReductionChart() {
  // Traditional cooking emissions (high, relatively flat)
  const traditionalPath = 'M 0,40 C 40,38 80,42 120,40 C 160,38 200,41 240,39 C 280,37 320,40 360,38 L 360,180 L 0,180 Z';
  // Improved cookstove emissions (declining over time)
  const improvedPath = 'M 0,40 C 40,45 80,60 120,80 C 160,100 200,120 240,135 C 280,145 320,150 360,152 L 360,180 L 0,180 Z';
  // The gap between = emission reductions (green area)
  const months = ['Jan', 'Mar', 'May', 'Jul', 'Sep', 'Nov'];
  const yLabels = ['4.0', '3.0', '2.0', '1.0', '0'];
  return <div className="bg-white rounded-2xl border border-carbon-geo p-6 sm:p-8 shadow-lg" data-id="element-1380">
      {/* Chart Header */}
      <div className="flex items-center justify-between mb-6" data-id="element-1381">
        <div data-id="element-1382">
          <p className="text-xs font-bold uppercase tracking-wider text-carbon-mid/60" data-id="element-1383">
            Annual Household Emissions
          </p>
          <p className="text-lg font-bold text-carbon-deep mt-0.5" data-id="element-1384">
            CO₂ Emissions Comparison
          </p>
        </div>
        <div className="flex items-center gap-4" data-id="element-1385">
          <div className="flex items-center gap-1.5" data-id="element-1386">
            <div className="w-3 h-3 rounded-sm bg-red-400/70" data-id="element-1387" />
            <span className="text-[11px] text-carbon-mid font-medium" data-id="element-1388">
              Traditional
            </span>
          </div>
          <div className="flex items-center gap-1.5" data-id="element-1389">
            <div className="w-3 h-3 rounded-sm bg-carbon-bright/70" data-id="element-1390" />
            <span className="text-[11px] text-carbon-mid font-medium" data-id="element-1391">
              With Cookstove
            </span>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="relative" data-id="element-1392">
        {/* Y-axis labels */}
        <div className="absolute left-0 top-0 bottom-6 flex flex-col justify-between pr-2" data-id="element-1393">
          {yLabels.map(label => <span key={label} className="text-[10px] text-gray-400 font-mono leading-none" data-id="element-1394">
              {label}t
            </span>)}
        </div>

        <div className="ml-8" data-id="element-1395">
          <svg viewBox="0 0 360 180" className="w-full h-auto" preserveAspectRatio="xMidYMid meet" data-id="element-1396">
            {/* Grid lines */}
            <line x1="0" y1="0" x2="360" y2="0" stroke="#E5E7EB" strokeWidth="0.5" strokeDasharray="4 4" data-id="element-1397" />
            <line x1="0" y1="45" x2="360" y2="45" stroke="#E5E7EB" strokeWidth="0.5" strokeDasharray="4 4" data-id="element-1398" />
            <line x1="0" y1="90" x2="360" y2="90" stroke="#E5E7EB" strokeWidth="0.5" strokeDasharray="4 4" data-id="element-1399" />
            <line x1="0" y1="135" x2="360" y2="135" stroke="#E5E7EB" strokeWidth="0.5" strokeDasharray="4 4" data-id="element-1400" />
            <line x1="0" y1="180" x2="360" y2="180" stroke="#E5E7EB" strokeWidth="0.5" data-id="element-1401" />

            {/* Traditional cooking area (red/warm) */}
            <path d={traditionalPath} fill="#EF4444" fillOpacity="0.12" data-id="element-1402" />
            <path d="M 0,40 C 40,38 80,42 120,40 C 160,38 200,41 240,39 C 280,37 320,40 360,38" fill="none" stroke="#EF4444" strokeWidth="2.5" strokeOpacity="0.6" data-id="element-1403" />

            {/* Improved cookstove area (green) - shows reduction */}
            <path d={improvedPath} fill="#52B788" fillOpacity="0.15" data-id="element-1404" />
            <path d="M 0,40 C 40,45 80,60 120,80 C 160,100 200,120 240,135 C 280,145 320,150 360,152" fill="none" stroke="#52B788" strokeWidth="2.5" data-id="element-1405" />

            {/* Reduction zone label arrow */}
            <defs data-id="element-1406">
              <linearGradient id="reduction-gradient" x1="0" y1="0" x2="0" y2="1" data-id="element-1407">
                <stop offset="0%" stopColor="#52B788" stopOpacity="0.25" data-id="element-1408" />
                <stop offset="100%" stopColor="#52B788" stopOpacity="0.05" data-id="element-1409" />
              </linearGradient>
            </defs>

            {/* Highlight the gap = emission reductions */}
            <path d="M 120,40 C 160,38 200,41 240,39 C 280,37 320,40 360,38 L 360,152 C 320,150 280,145 240,135 C 200,120 160,100 120,80 Z" fill="url(#reduction-gradient)" data-id="element-1410" />

            {/* Annotation: arrow + label for the reduction gap */}
            <line x1="280" y1="39" x2="280" y2="143" stroke="#52B788" strokeWidth="1" strokeDasharray="3 3" data-id="element-1411" />
            <circle cx="280" cy="39" r="3" fill="#EF4444" data-id="element-1412" />
            <circle cx="280" cy="143" r="3" fill="#52B788" data-id="element-1413" />

            {/* Reduction label */}
            <rect x="220" y="78" width="120" height="24" rx="6" fill="#1B4332" fillOpacity="0.9" data-id="element-1414" />
            <text x="280" y="94" textAnchor="middle" fill="white" fontSize="10" fontWeight="700" fontFamily="Inter, system-ui, sans-serif" data-id="element-1415">
              −2.8 tCO₂e/year
            </text>
          </svg>

          {/* X-axis labels */}
          <div className="flex justify-between mt-1 px-0" data-id="element-1416">
            {months.map(m => <span key={m} className="text-[10px] text-gray-400 font-mono" data-id="element-1417">
                {m}
              </span>)}
          </div>
        </div>
      </div>

      {/* Bottom insight bar */}
      <div className="mt-6 flex items-center gap-3 p-3 rounded-xl bg-carbon-bright/5 border border-carbon-bright/15" data-id="element-1418">
        <div className="w-8 h-8 rounded-lg bg-carbon-bright/15 flex items-center justify-center flex-shrink-0" data-id="element-1419">
          <TreesIcon className="w-4 h-4 text-carbon-bright" data-id="element-1420" />
        </div>
        <p className="text-xs text-carbon-mid leading-snug" data-id="element-1421">
          <span className="font-bold text-carbon-deep" data-id="element-1422">
            Each stove prevents 2.8 tons of CO₂ annually
          </span>
          . That's equivalent to planting 46 trees or removing a car from the
          road for 7 months.
        </p>
      </div>
    </div>;
}
/* ─── Main Page ─── */
export function CookstoveCredits() {
  const statsRef = useRef(null);
  const statsInView = useInView(statsRef, {
    once: true,
    margin: '-60px'
  });
  const impactRef = useRef(null);
  const impactInView = useInView(impactRef, {
    once: true,
    margin: '-80px'
  });
  const featuresRef = useRef(null);
  const featuresInView = useInView(featuresRef, {
    once: true,
    margin: '-80px'
  });
  const platformRef = useRef(null);
  const platformInView = useInView(platformRef, {
    once: true,
    margin: '-60px'
  });
  const ctaRef = useRef(null);
  const ctaInView = useInView(ctaRef, {
    once: true,
    margin: '-60px'
  });
  const features = [{
    icon: 'FlameIcon',
    title: 'Usage Monitoring',
    description: 'IoT-enabled stove sensors track actual usage hours, fuel consumption, and cooking patterns, replacing unreliable survey-based methods.'
  }, {
    icon: 'UsersIcon',
    title: 'Health Impact',
    description: 'Household air pollution kills 13,000+ Ugandans annually. Verified cookstove adoption directly reduces PM2.5 exposure by up to 60%.'
  }, {
    icon: 'TreesIcon',
    title: 'Deforestation Prevention',
    description: "Each improved cookstove saves 2-4 tons of wood per year, reducing pressure on Uganda's rapidly shrinking forest cover."
  }, {
    icon: 'GlobeIcon',
    title: 'Carbon Quantification',
    description: 'Precise emission reduction calculations using AMS-II.G methodology, from baseline surveys to real-time fuel savings data.'
  }, {
    icon: 'ShieldCheckIcon',
    title: 'Credit Integrity',
    description: 'Digital verification eliminates double-counting and ghost credits. Every ton of CO₂ reduced is traceable to a specific household and stove.'
  }, {
    icon: 'ScaleIcon',
    title: 'Market Access',
    description: 'Gold Standard and Verra-compliant DMRV opens access to premium voluntary carbon markets paying $15-25 per ton.'
  }];
  const stats = [{
    value: '38M+',
    label: 'People Affected',
    detail: 'Ugandans exposed to household air pollution from traditional cooking methods'
  }, {
    value: '95%',
    label: 'Biomass Dependent',
    detail: 'Of households use firewood or charcoal as primary cooking fuel'
  }, {
    value: '4.3M',
    label: 'Tons CO₂/year',
    detail: 'Potential emission reductions from nationwide cookstove adoption'
  }, {
    value: '$120M+',
    label: 'Credit Potential',
    detail: 'Annual voluntary carbon market value from Ugandan cookstove projects'
  }];
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 40
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  return <>
      {/* ═══ HERO ═══ */}
      <section className="relative w-full overflow-hidden bg-[#FAFDF7]" data-id="element-1423">
        <svg className="absolute inset-0 w-full h-full opacity-[0.04]" aria-hidden="true" data-id="element-1424">
          <defs data-id="element-1425">
            <pattern id="cook-hero-grid" width="50" height="50" patternUnits="userSpaceOnUse" patternTransform="rotate(45)" data-id="element-1426">
              <line x1="0" y1="0" x2="0" y2="50" stroke="#1B4332" strokeWidth="0.5" data-id="element-1427" />
              <line x1="0" y1="0" x2="50" y2="0" stroke="#1B4332" strokeWidth="0.5" data-id="element-1428" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#cook-hero-grid)" data-id="element-1429" />
        </svg>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 lg:py-36" data-id="element-1430">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-1431">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.7,
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-1432">
              <p className="text-sm font-semibold uppercase tracking-widest text-carbon-bright mb-4" data-id="element-1433">
                Cookstove Carbon Credits | Uganda
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-carbon-deep tracking-tight leading-[1.1]" data-id="element-1434">
                Verified Carbon Credits From{' '}
                <span className="relative inline-block" data-id="element-1435">
                  <span className="relative z-10" data-id="element-1436">Clean Cooking</span>
                  <span className="absolute bottom-1 left-0 right-0 h-[40%] bg-carbon-bright/25 -z-0 rounded-sm" aria-hidden="true" data-id="element-1437" />
                </span>{' '}
                Solutions
              </h1>
              <p className="mt-6 text-lg text-carbon-mid leading-relaxed max-w-xl" data-id="element-1438">
                Over 95% of Ugandan households rely on biomass for cooking,
                creating massive health and environmental impacts. CarbonSafe
                provides end-to-end DMRV for improved cookstove projects, from
                distribution tracking to emission reduction verification.
              </p>
              <div className="mt-5 flex flex-wrap gap-3" data-id="element-1439">
                {['Gold Standard', 'Verra VCS', 'GHG Protocol'].map(badge => <span key={badge} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-carbon-geo bg-white text-sm font-medium text-carbon-deep" data-id="element-1440">
                    <span className="w-2 h-2 rounded-full bg-carbon-bright" data-id="element-1441" />
                    {badge}
                  </span>)}
              </div>
              <div className="mt-8 flex flex-col sm:flex-row gap-4" data-id="element-1442">
                <a href="#" className="inline-flex items-center gap-2 px-7 py-3.5 bg-carbon-bright text-white text-base font-semibold rounded-lg hover:bg-carbon-light transition-colors shadow-lg shadow-carbon-bright/20" data-id="element-1443">
                  See CarbonSafe in action
                  <ArrowRightIcon className="w-4 h-4" data-id="element-1444" />
                </a>
                <a href="#" className="inline-flex items-center gap-2 px-7 py-3.5 border-2 border-carbon-bright text-carbon-deep text-base font-semibold rounded-lg hover:bg-carbon-bright/5 transition-colors" data-id="element-1445">
                  Explore The Platform
                </a>
              </div>
            </motion.div>

            {/* Hexagon Visual with Cookstove Image */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            delay: 0.2,
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-1446">
              <CookstoveHexagonVisual data-id="element-1447" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ STATS ═══ */}
      <section ref={statsRef} className="w-full bg-carbon-deep py-20 sm:py-24" data-id="element-1448">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1449">
          <motion.div className="text-center mb-14" initial={{
          opacity: 0,
          y: 20
        }} animate={statsInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-1450">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight" data-id="element-1451">
              The clean cooking crisis in numbers
            </h2>
            <p className="mt-3 text-lg text-white/60 max-w-2xl mx-auto" data-id="element-1452">
              Uganda faces one of the world's most urgent clean cooking
              challenges, with massive carbon credit potential.
            </p>
          </motion.div>
          <motion.div className="grid grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8" initial="hidden" animate={statsInView ? 'visible' : 'hidden'} variants={{
          hidden: {},
          visible: {
            transition: {
              staggerChildren: 0.12
            }
          }
        }} data-id="element-1453">
            {stats.map(stat => <motion.div key={stat.label} variants={{
            hidden: {
              opacity: 0,
              y: 30
            },
            visible: {
              opacity: 1,
              y: 0,
              transition: {
                duration: 0.6
              }
            }
          }} data-id="element-1454">
                <AnimatedStat {...stat} data-id="element-1455" />
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* ═══ HOW CARBONSAFE WORKS ═══ */}
      <HowCarbonSafeWorks verticalName="cookstove credits" subtext="From sensor data to carbon credits, four steps to verified emission reductions." steps={[{
      icon: 'CpuIcon',
      title: 'IoT Sensor Deployment',
      description: 'Temperature and usage sensors installed on improved cookstoves track real cooking activity.'
    }, {
      icon: 'ActivityIcon',
      title: 'Usage Monitoring',
      description: 'Continuous data on cooking hours, fuel savings, and emission reductions per household.'
    }, {
      icon: 'BarChart3Icon',
      title: 'Emission Quantification',
      description: 'AMS-II.G methodology calculations convert usage data into verified tCO₂e reductions.'
    }, {
      icon: 'ShieldCheckIcon',
      title: 'Carbon Credit Issuance',
      description: 'Gold Standard or Verra-verified credits issued with full digital audit trail.'
    }]} data-id="element-1456" />

      {/* ═══ IMPACT IMAGE SECTION ═══ */}
      <section ref={impactRef} className="w-full bg-white py-20 sm:py-28 border-t border-b border-carbon-geo" data-id="element-1457">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1458">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-1459">
            {/* Left: Emission Reduction Chart */}
            <motion.div initial={{
            opacity: 0,
            x: -40
          }} animate={impactInView ? {
            opacity: 1,
            x: 0
          } : {}} transition={{
            duration: 0.7,
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-1460">
              <EmissionReductionChart data-id="element-1461" />
            </motion.div>

            {/* Right: Narrative */}
            <motion.div initial={{
            opacity: 0,
            x: 40
          }} animate={impactInView ? {
            opacity: 1,
            x: 0
          } : {}} transition={{
            duration: 0.7,
            delay: 0.15,
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-1462">
              <span className="inline-block text-[11px] font-bold uppercase tracking-widest text-carbon-bright bg-carbon-bright/10 px-3 py-1 rounded-full mb-5" data-id="element-1463">
                Real-World Impact
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-carbon-deep tracking-tight leading-tight" data-id="element-1464">
                From open fires to verified emission reductions
              </h2>
              <p className="mt-5 text-base text-carbon-mid leading-relaxed" data-id="element-1465">
                In rural Uganda, traditional three-stone fires consume 2-3 tons
                of wood per household annually, contributing to deforestation
                and exposing families to dangerous levels of indoor air
                pollution. CarbonSafe's DMRV platform tracks every improved
                cookstove from distribution to daily usage, creating a verified
                digital record that transforms clean cooking adoption into
                high-integrity carbon credits.
              </p>

              {/* Mini Stats */}
              <div className="mt-8 grid grid-cols-3 gap-6" data-id="element-1466">
                <div data-id="element-1467">
                  <p className="text-2xl sm:text-3xl font-extrabold text-carbon-bright" data-id="element-1468">
                    60%
                  </p>
                  <p className="mt-1 text-sm text-carbon-mid font-medium" data-id="element-1469">
                    Less PM2.5 exposure
                  </p>
                </div>
                <div data-id="element-1470">
                  <p className="text-2xl sm:text-3xl font-extrabold text-carbon-bright" data-id="element-1471">
                    2-4 tons
                  </p>
                  <p className="mt-1 text-sm text-carbon-mid font-medium" data-id="element-1472">
                    Wood saved per stove/year
                  </p>
                </div>
                <div data-id="element-1473">
                  <p className="text-2xl sm:text-3xl font-extrabold text-carbon-bright" data-id="element-1474">
                    $15-25
                  </p>
                  <p className="mt-1 text-sm text-carbon-mid font-medium" data-id="element-1475">
                    Per ton credit value
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ FEATURES ═══ */}
      <section ref={featuresRef} className="w-full bg-white py-20 sm:py-28 border-b border-carbon-geo" data-id="element-1476">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1477">
          <motion.div className="text-center mb-14" initial={{
          opacity: 0,
          y: 20
        }} animate={featuresInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-1478">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-carbon-deep tracking-tight" data-id="element-1479">
              Why cookstove DMRV is{' '}
              <span className="text-carbon-bright" data-id="element-1480">mission-critical</span>
            </h2>
            <p className="mt-4 text-lg text-carbon-mid max-w-2xl mx-auto" data-id="element-1481">
              Without robust digital MRV, cookstove carbon credits face
              credibility challenges that undermine the entire clean cooking
              movement.
            </p>
          </motion.div>
          <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" initial="hidden" animate={featuresInView ? 'visible' : 'hidden'} variants={{
          hidden: {},
          visible: {
            transition: {
              staggerChildren: 0.1
            }
          }
        }} data-id="element-1482">
            {features.map(feature => {
            const IconComponent = iconMap[feature.icon];
            return <motion.article key={feature.title} className="p-7 rounded-2xl border border-carbon-geo bg-[#FAFDF7]" variants={cardVariants} data-id="element-1483">
                  <div className="w-14 h-14 rounded-xl bg-carbon-bright/10 flex items-center justify-center mb-5" data-id="element-1484">
                    {IconComponent && <IconComponent className="w-7 h-7 text-carbon-bright" data-id="element-1485" />}
                  </div>
                  <h3 className="text-lg font-bold text-carbon-deep mb-2" data-id="element-1486">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-carbon-mid leading-relaxed" data-id="element-1487">
                    {feature.description}
                  </p>
                </motion.article>;
          })}
          </motion.div>
        </div>
      </section>

      {/* ═══ PLATFORM PREVIEW ═══ */}
      <section ref={platformRef} className="w-full bg-[#FAFDF7] py-20 sm:py-28" data-id="element-1488">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1489">
          <motion.div className="text-center mb-14" initial={{
          opacity: 0,
          y: 20
        }} animate={platformInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-1490">
            <span className="inline-block text-[11px] font-bold uppercase tracking-widest text-carbon-bright bg-carbon-bright/10 px-3 py-1 rounded-full mb-5" data-id="element-1491">
              Platform Preview
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-carbon-deep tracking-tight" data-id="element-1492">
              End-to-end cookstove DMRV in{' '}
              <span className="text-carbon-bright" data-id="element-1493">one dashboard</span>
            </h2>
            <p className="mt-4 text-lg text-carbon-mid max-w-2xl mx-auto" data-id="element-1494">
              From IoT sensor data to verified carbon credits: monitor,
              quantify, and report with confidence.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 60
        }} animate={platformInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.8,
          delay: 0.2,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-1495">
            <DashboardMockup data-id="element-1496" />
          </motion.div>
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section ref={ctaRef} className="w-full py-20 sm:py-28 bg-[#FAFDF7]" data-id="element-1497">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1498">
          <motion.div className="relative overflow-hidden rounded-3xl px-8 py-16 sm:px-16 sm:py-20 text-center" style={{
          background: 'linear-gradient(135deg, #2D6A4F 0%, #1B4332 100%)'
        }} initial={{
          opacity: 0,
          y: 40
        }} animate={ctaInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.7,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-1499">
            <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-10" style={{
            background: 'radial-gradient(circle, #52B788, transparent)'
          }} aria-hidden="true" data-id="element-1500" />
            <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full opacity-10" style={{
            background: 'radial-gradient(circle, #6BCB77, transparent)'
          }} aria-hidden="true" data-id="element-1501" />
            <h2 className="relative text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-tight max-w-3xl mx-auto" data-id="element-1502">
              Every stove tells a story.
              <br data-id="element-1503" />
              <span className="text-carbon-bright" data-id="element-1504">Make yours verified.</span>
            </h2>
            <p className="relative mt-5 text-lg text-white/70 max-w-xl mx-auto" data-id="element-1505">
              Join cookstove project developers using CarbonSafe to generate
              high-integrity carbon credits with digital MRV.
            </p>
            <div className="relative mt-10 flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-1506">
              <a href="#" className="inline-flex items-center gap-2 px-8 py-4 bg-carbon-bright text-white text-base font-semibold rounded-lg hover:bg-carbon-light transition-colors shadow-lg shadow-black/20" data-id="element-1507">
                Start Your Cookstove Project
                <ArrowRightIcon className="w-4 h-4" data-id="element-1508" />
              </a>
              <a href="#" className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/30 text-white text-base font-semibold rounded-lg hover:bg-white/10 transition-colors" data-id="element-1509">
                Download Case Study
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer data-id="element-1510" />
    </>;
}